import { Injectable, Injector } from '@angular/core';
import { AngularFirestoreCollection } from '@angular/fire/firestore';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Extender } from 'src/shared/helpers/extender';
import { Account, Conversation } from 'src/shared/models';
@Injectable({
  providedIn: 'root'
})
export class ChatService extends Extender {
  public conversationsStoreRef: AngularFirestoreCollection<Conversation>;
  public user: Account;

  constructor(protected injector: Injector) {
    super(injector);
    this.auth$.user$.subscribe((user) => {
      this.user = user;
    });
    this.conversationsStoreRef = this.afStore$.collection('conversations');
  }

  /**
   * map conversations based on current logged in user so that if the
   * user is the sender, thier recipient conversations are shown and
   * if user is the recipient, show senders conversation
   * @method getConversations
   * @param {Conversation[]} conversations
   */
  public getConversations() {
    const reads$ = [];
    return this.conversationsStoreRef.snapshotChanges().pipe(
      switchMap((actions) => {
        if (actions.length > 0) {
          actions.map((a) => {
            reads$.push(this.getConversation(a.payload.doc.id));
          });
          return combineLatest(reads$);
        } else {
          return of([]);
        }
      }),
      map((data: Conversation[]) => {
        return data;
      })
    );
  }

  /**
   * get a single conversation and its user and recipient
   * @param id
   */
  public getConversation(id): Observable<any> {
    let convo: Conversation;
    return this.conversationsStoreRef
      .doc(id)
      .snapshotChanges()
      .pipe(
        switchMap((doc: any) => {
          convo = {
            id: doc.payload.id,
            ...(doc.payload.data() as Conversation)
          };
          id = convo.uid === this.user.uid ? (id = convo.rid) : convo.uid;
          return this.afStore$.doc(`users/${id}`).valueChanges();
        }),
        map((recipient) => {
          return { ...convo, recipient, user: this.user };
        })
      );
  }

  public deleteConversation(id) {
    return this.conversationsStoreRef.doc(id).delete();
  }

  /**
   * @description open chat with a  contact, if one does not already exist, create one, if one exist, use it,
   * find message that already exists if exist go to chat page with found conversation as context,
   * if conversation has been found, if conversation hasnt been found create one with sender and recipient details, get conversation context and send it to chat page
   * @method chat
   * @param contact Account
   * @public
   * @returns void
   */
  public chat(contact: Account): Observable<any> {
    return this.afStore$
      .collection('conversations', (ref) =>
        ref.where('uid', '==', this.user.uid).where('rid', '==', contact.uid)
      )
      .get()
      .pipe(
        switchMap((docs) => {
          if (!docs.empty) {
            return of(docs.docs[0]);
          } else {
            return this.addConversation(contact);
          }
        }),
        map((_convo: any) => {
          return _convo.id;
        })
      );
  }

  /**
   * add a conversation
   * @param contact
   */
  public addConversation(contact) {
    return this.conversationsStoreRef.add({
      uid: this.user.uid,
      rid: contact.uid,
      isArchived: false,
      createdAt: Date.now(),
      messages: []
    });
  }
}
